import { css } from '@emotion/react';
import { useContext } from 'react';
import { IIllustratedText } from '../../types/@types/generated/contentful';
import { Color, colors } from '../../utils/styleguide';
import BuildingBlockInfo from '../component-library/layout/BuildingBlockInfo';
import HighlightSectionLayout from '../component-library/layout/HighlightSection/HighlightSectionLayout';
import LazyLoadStyle from '../component-library/LazyLoadStyle';
import LottiePlayer from '../component-library/LottiePlayer';
import SectionBackground from '../component-library/SectionBackground';
import StoreInfo from '../component-library/StoreInfo';
import { TemplatingContext } from './contexts/TemplatingContext';
import Image, { ImageStyleProps } from './Image';
import { PageSectionComponent } from './pageSections';
import RichText from './RichText';
import sectionToCompatibleProps from './util/sectionToCompatibleProps';

const HighlightSection: PageSectionComponent<null> = ({ section, page }) => {
  const { references, settings, variant } = section.fields;
  const storesInfo = section.fields?.buildingBlock?.fields?.storesInfo;
  const {
    id,
    backgroundColor,
    backgroundImage,
    backgroundImageLandscape,
    backgroundImagePortrait,
    reversed,
    title,
    description,
    button,
    media,
  } = sectionToCompatibleProps({ section });
  const headlineColor = colors[settings?.headlineColor as Color];
  const descriptionColor = colors[settings?.descriptionColor as Color];
  const { templateReplacer } = useContext(TemplatingContext);
  const href = button.href && templateReplacer(button.href);

  const campaign = page.fields?.category?.fields.name;
  const storesInfoComponent = (
    <StoreInfo
      creative="highlight section"
      campaign={campaign}
      storesInfo={storesInfo}
      variant={variant}
    />
  );

  return (
    <SectionBackground
      id={id}
      backgroundColor={backgroundColor}
      backgroundImage={{
        landscape: backgroundImageLandscape?.fields.file.url ?? backgroundImage?.fields.file.url,
        portrait: backgroundImagePortrait?.fields.file.url,
      }}
    >
      <HighlightSectionLayout
        reversed={reversed}
        fixedMediaHeight={media.fixedHeight}
        dynamicMediaHeight={media.dynamicHeight}
        content={
          <BuildingBlockInfo
            title={<RichText document={title} textColor={headlineColor} />}
            description={
              <RichText
                document={description}
                textColor={descriptionColor}
                withCheckmark={settings?.withCheckmark as boolean}
              />
            }
            buttonLabel={button.label}
            buttonPrompt={button.prompt}
            buttonColor={button.colorContext}
            largeButton={button.size === 'large'}
            content={storesInfoComponent}
            Logo={
              (references &&
                references.length === 1 &&
                ((props: ImageStyleProps) => (
                  <Image
                    asset={(references as unknown as IIllustratedText[])[0].fields.image}
                    {...props}
                  />
                ))) ||
              undefined
            }
            href={href}
          />
        }
        media={
          media.image ? (
            <Image
              asset={media.image}
              wrapperCss={css`
                height: 100%;
                width: 100%;
                display: flex;
                align-items: center;
              `}
              css={css`
                height: 100%;
                width: 100%;
                object-fit: ${settings?.imageFit ?? 'contain'};
              `}
            />
          ) : (
            (media.lottieAnimationDesktop || media.lottieAnimationMobile) && (
              <LazyLoadStyle
                wrapperCss={css`
                  height: 100%;
                  width: 100%;
                  display: flex;
                  align-items: center;
                `}
              >
                <LottiePlayer
                  desktopPath={media.lottieAnimationDesktop}
                  mobilePath={media.lottieAnimationMobile}
                />
              </LazyLoadStyle>
            )
          )
        }
      />
    </SectionBackground>
  );
};

export default HighlightSection;
